const normalizeToken = (value) => String(value ?? "").trim().toLowerCase();

const interpolateText = (template, values = {}) =>
  Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, String(value ?? "")),
    template || ""
  );

const parseMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_error) {
      return {};
    }
  }
  return typeof metadata === "object" ? metadata : {};
};

const pickDisplayValue = (...values) => {
  const found = values.find((value) => {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 && normalized !== "-";
  });
  return found == null ? "" : String(found).trim();
};

const inferNotificationKind = (notification, metadata) => {
  const type = normalizeToken(notification?.type);
  const title = normalizeToken(notification?.title);
  const planStatus = normalizeToken(metadata?.plan_status);
  const action = normalizeToken(metadata?.action);
  const kind = normalizeToken(metadata?.kind);

  if (kind === "template_datastream_cleanup") {
    return "";
  }

  if (type === "create_request" || title.includes("activation request")) {
    return "activationRequest";
  }

  if (
    type === "plan_updated" ||
    title.includes("device plan") ||
    title.includes("plan expired") ||
    title.includes("plan disabled")
  ) {
    if (planStatus === "expired" || title.includes("expired")) return "planExpired";
    if (planStatus === "disabled" || action === "disable" || title.includes("disabled")) {
      return "planDisabled";
    }
    if (planStatus === "pending_activation" || title.includes("pending")) {
      return "planPending";
    }
    if (
      planStatus === "active" &&
      ["enable", "activate", "renew", "renewal", "extend", "change"].includes(action)
    ) {
      return "planActive";
    }
    return "planUpdated";
  }

  return "";
};

export const localizeSystemNotification = (notification, translations = {}) => {
  if (!notification || typeof notification !== "object") {
    return notification;
  }

  const metadata = parseMetadata(notification.metadata);
  const textT = translations.notificationText || {};
  const titlesT = textT.titles || {};
  const messagesT = textT.messages || {};

  const deviceName = pickDisplayValue(
    notification?.device?.name,
    notification?.device_name,
    metadata?.device_name,
    notification?.did,
    metadata?.did
  );
  const ownerName = pickDisplayValue(
    notification?.device?.owner?.name,
    notification?.device?.owner?.email,
    notification?.owner_name,
    metadata?.owner_name
  );
  const requesterName = pickDisplayValue(
    metadata?.requested_by_name,
    metadata?.requested_by_email
  );
  const did = pickDisplayValue(notification?.device?.did, notification?.did, metadata?.did);
  const durationPlan = pickDisplayValue(metadata?.duration_plan, notification?.duration_plan);

  const values = {
    device: deviceName || textT.unknownDevice || did || "-",
    owner: ownerName || textT.unknownOwner || "-",
    requester: requesterName || textT.unknownRequester || "-",
    did: did || "-",
    days: durationPlan || "-",
  };

  const kind = inferNotificationKind(notification, metadata);

  const localizedTitle =
    (kind && titlesT[kind]) ||
    notification.title ||
    textT.fallbackTitle ||
    "Notification";
  const localizedMessage =
    (kind && messagesT[kind] ? interpolateText(messagesT[kind], values) : "") ||
    notification.message ||
    "";

  return {
    ...notification,
    metadata,
    title: localizedTitle,
    message: localizedMessage,
  };
};

export const formatSystemNotificationTimestamp = (value, lang = "en") => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year}, ${hours}:${minutes}`;
};
